/**
 * Generates a basic bar plot specification for Vega-Lite based on the selected data.
 * The plot includes dynamically determined X and Y units, headers, and a tool tip.
 *
 * @param {Array} selectedData - The data selected from the spreadsheet, where each row represents a data point.
 * @returns {Object} - Returns the Vega-Lite specification for the bar plot along with plot metadata (unitX, unitY, headerX, headerY).
 */
export const BarPlotBasic = (selectedData) => {
  const values = []; // Array to store formatted plot data
  let unitX;
  let unitY;
  let headerX;
  let headerY;

  /**
   * Iterates over each row of selected data to process and extract plot values, units, and headers.
   */
  selectedData.map((row, rowIndex) => {
    /**
     * Adds a new value to the `values` array, processing both X and Y coordinates.
     * Ensures that empty or missing data is handled gracefully.
     */
    const newValue = () => {
      const value = {};
      value.x = row[0]
        ? Object.prototype.hasOwnProperty.call(row[0], "m")
          ? row[0].m
          : " "
        : " "; // X value (nominal)
      value.y = row[1]
        ? Object.prototype.hasOwnProperty.call(row[1], "m")
          ? parseFloat(row[1].m)
          : 0
        : 0; // Y value (quantitative)
      values.push(value);
    };

    /**
     * Checks and sets the unit for X and Y axes by inspecting the cell's format.
     * Uses the format string (e.g., "%" for percentages) to determine the unit.
     */
    const checkUnits = () => {
      const setFormat = (format) => {
        if (format.includes("%")) {
          return "%"; // Sets unit as percentage if format includes "%"
        } else {
          return null;
        }
      };

      if (!unitX) {
        const format = row[0]
          ? Object.prototype.hasOwnProperty.call(row[0], "ct")
            ? row[0].ct.fa
            : ""
          : "";
        unitX = setFormat(format);
      }

      if (!unitY) {
        const format = row[1]
          ? Object.prototype.hasOwnProperty.call(row[1], "ct")
            ? row[1].ct.fa
            : ""
          : "";
        unitY = setFormat(format);
      }
    };

    /**
     * Processes the first row to determine headers or units if it's not numeric.
     * Subsequent rows are processed as data points for the plot.
     */
    if (rowIndex === 0 && row[0] && row[1]) {
      if (isNaN(parseFloat(row[0].m)) && isNaN(parseFloat(row[1].m))) {
        headerX = row[0].m; // Sets header for the X-axis
        headerY = row[1].m; // Sets header for the Y-axis
      } else {
        checkUnits();
        newValue();
      }
    } else if (row[0] || row[1]) {
      if (!unitX || !unitY) {
        checkUnits();
      }
      newValue();
    }
  });

  /**
   * Returns the Vega-Lite specification for the bar plot.
   * The plot displays the selected data, with optional headers and units.
   */
  return {
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      padding: 5,
      autosize: {
        type: "fit",
        resize: true,
        contains: "padding",
      },
      data: {
        values: values,
      },
      mark: {
        type: "bar",
        tooltip: true,
        color: "#003e6b", // Bar color
      },
      encoding: {
        x: {
          field: "x",
          type: "nominal", // Nominal data for X-axis
          axis: {
            labelAngle: -90, // Rotate X-axis labels
            labelAlign: "right",
            labelBaseline: "middle",
            title: null, // No title for X-axis
          },
        },
        y: {
          field: "y",
          type: "quantitative", // Quantitative data for Y-axis
          axis: {
            grid: true,
            labelExpr: `datum.value + '${unitY ? unitY : ""}'`, // Append unit to Y-axis labels if available
            title: null, // No title for Y-axis
          },
        },
        tooltip: [
          { field: "y", type: "nominal", title: `${headerY ? headerY : "y"}` }, // Tooltip shows Y-axis header or default 'y'
        ],
      },
    },
    unitX, // Optional unit for X-axis
    unitY, // Optional unit for Y-axis
    headerX, // Header label for X-axis
    headerY, // Header label for Y-axis
  };
};
